import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Shield, MessageSquare, Sparkles, ShieldCheck, Zap, ArrowRight, AlertTriangle,
  Eye, UserX, Flame, ShieldAlert, Brain, Scale, Quote, Radar, DollarSign, Lock,
  Download, BarChart3, Check,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { OptimizedImage } from "@/components/ui/optimized-image";
import dashboardImage from "@/assets/dashboard-preview.png";
import testimonial1 from "@/assets/testimonial-sarah.jpg";
import testimonial2 from "@/assets/testimonial-james.jpg";
import testimonial3 from "@/assets/testimonial-david.jpg";
import { scrollToSection } from "@/lib/smoothScroll";
import { SEOHead } from "@/components/seo/SEOHead";
import { OrganizationSchema } from "@/components/seo/OrganizationSchema";
import { SourcesCitation } from "@/components/marketing/SourcesCitation";
import { PricingPreview } from "@/components/marketing/PricingPreview";
import { ComparisonTable } from "@/components/marketing/ComparisonTable";
import { StickyCTA } from "@/components/marketing/StickyCTA";
import { SocialProofPopup } from "@/components/marketing/SocialProofPopup";
import { EditorialNav } from "@/components/marketing/editorial/EditorialNav";
import { EditorialFooter } from "@/components/marketing/editorial/EditorialFooter";
import { EditorialHero } from "@/components/marketing/editorial/EditorialHero";
import { EditorialArtifact } from "@/components/marketing/editorial/EditorialArtifact";
import { EditorialSection } from "@/components/marketing/editorial/EditorialSection";
import { EditorialCard } from "@/components/marketing/editorial/EditorialCard";
import { EditorialLogoBar } from "@/components/marketing/editorial/EditorialLogoBar";
import { EditorialROICalculator } from "@/components/marketing/editorial/EditorialROICalculator";
import { EditorialFounderLetter } from "@/components/marketing/editorial/EditorialFounderLetter";
import { EditorialFeedbackTicker } from "@/components/marketing/editorial/EditorialFeedbackTicker";


const Index = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (window.location.hash) {
      const sectionId = window.location.hash.substring(1);
      scrollToSection(sectionId);
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans antialiased selection:bg-primary/15 selection:text-primary overflow-x-hidden">
      <SEOHead
        title="SendFeedback.ai - 100% Anonymous Employee Feedback Platform"
        description="Build trust and catch retention risks early with truly anonymous employee feedback. AI-powered insights, 5-minute setup, no tracking. Start free."
        canonicalUrl="https://sendfeedback.ai"
        ogImage="/og-images/og-home.jpg"
        keywords="anonymous employee feedback, employee engagement, workplace feedback, HR software, employee surveys, retention risk, workplace culture"
      />
      <OrganizationSchema />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "SendFeedback - 100% Anonymous Employee Feedback Platform",
          applicationCategory: "BusinessApplication",
          description: "The only truly anonymous employee feedback platform. No names, no emails, no tracking—guaranteed privacy with optional department context for actionable insights.",
          url: "https://sendfeedback.ai",
          image: "https://sendfeedback.ai/dashboard-preview.png",
          operatingSystem: "Web",
          offers: [
            { "@type": "Offer", name: "Starter Plan", price: "39", priceCurrency: "USD", description: "100 anonymous submissions per month with basic AI sentiment analysis. 15-day free trial." },
            { "@type": "Offer", name: "Professional Plan", price: "79", priceCurrency: "USD", description: "Unlimited anonymous submissions with advanced AI insights, HR guidance, and response templates" },
            { "@type": "Offer", name: "Business Plan", price: "149", priceCurrency: "USD", description: "Unlimited pages and anonymous submissions with custom domain, white-label, and advanced HR support" },
          ],
          featureList: [
            "Guaranteed 100% anonymity - no exceptions",
            "Optional department tagging for context",
            "No login, no tracking, no IP collection",
            "AI-powered feedback clustering and sentiment analysis",
            "AI HR Support with risk classification and response templates",
            "Action tracker with kanban board for closing the loop",
            "Database-level enforcement prevents PII collection",
            "Legal guardrails and psychology-safe language guidance",
            "Export data in multiple formats",
          ],
        })}
      </script>

      <EditorialNav />
      <SocialProofPopup />

      {/* FAQ schema for rich results */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            { "@type": "Question", name: "What's the ROI?", acceptedAnswer: { "@type": "Answer", text: "If this tool prevents even one regrettable departure, you've saved significant costs — SHRM estimates turnover costs 50–200% of annual salary in recruiting, training, and lost productivity." } },
            { "@type": "Question", name: "Won't this create more problems than it solves?", acceptedAnswer: { "@type": "Answer", text: "The opposite. You already have these problems — you just don't see them yet. Anonymous feedback surfaces issues while they're still fixable." } },
            { "@type": "Question", name: "How is this different from engagement surveys?", acceptedAnswer: { "@type": "Answer", text: "Engagement surveys are like yearly physicals — they catch what's already wrong. SendFeedback is a continuous health monitor that spots issues while they're still treatable." } },
            { "@type": "Question", name: "What if people abuse anonymity?", acceptedAnswer: { "@type": "Answer", text: "Our AI-powered abuse detection and PII filtering keeps feedback constructive. People want to help, not hurt — when they have a safe channel." } },
            { "@type": "Question", name: "How is anonymity actually protected?", acceptedAnswer: { "@type": "Answer", text: "We don't collect IP addresses, browser fingerprints, login credentials, or any identifying metadata. Even we can't tell who submitted what." } },
            { "@type": "Question", name: "Can we export and own our data?", acceptedAnswer: { "@type": "Answer", text: "Yes. Export feedback, summaries, and insights anytime in multiple formats. You own your data completely — no lock-in, no limitations." } },
          ],
        })}
      </script>

      <main id="main-content">
        {/* HERO */}
        <EditorialHero />

        {/* TRUST BAR */}
        <EditorialLogoBar />

        {/* ROI CALCULATOR */}
        <EditorialROICalculator />

        {/* THE ARTIFACT — anonymous report visualization */}
        <EditorialArtifact />

        {/* SAMPLE FEEDBACK TICKER */}
        <EditorialFeedbackTicker />

        {/* DASHBOARD PREVIEW — keep the screenshot but framed editorially */}
        <section className="relative px-4 sm:px-6 md:px-10 pb-16 sm:pb-20 md:pb-28 pt-12 sm:pt-16 md:pt-24 overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-0 opacity-60"
            style={{
              background:
                "radial-gradient(900px 500px at 100% 0%, hsl(50 60% 92% / 0.55), transparent 60%), radial-gradient(700px 420px at 0% 100%, hsl(150 30% 90% / 0.4), transparent 60%)",
            }}
          />
          <div className="relative max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/70 mb-6 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)]">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                The HR Operator's Console
              </span>
              <h2 className="font-sans text-3xl sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.02em] font-medium text-foreground mb-5">
                Built for People teams of one (or five),{" "}
                <span className="font-serif italic font-light text-foreground/75">
                  not enterprise committees.
                </span>
              </h2>
              <p className="font-sans text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                One screen. Themes, sentiment, risk flags, and AI-drafted responses &mdash; so you can act on Monday, not after the next quarterly survey cycle.
              </p>
            </div>
            <div className="relative rounded-3xl bg-card/80 backdrop-blur border border-border/60 p-2 md:p-3 shadow-[0_40px_80px_-30px_rgba(21,34,27,0.18)]">
              {/* Editorial browser chrome */}
              <div className="flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                  <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                </div>
                <div className="hidden sm:flex items-center gap-1.5 rounded-full bg-secondary/70 px-3 py-1 text-[10px] font-medium text-foreground/55 tracking-wide">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                  app.sendfeedback.ai / dashboard
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-foreground/40">
                  Live preview
                </span>
              </div>
              <div className="rounded-2xl border border-border/40 overflow-hidden">
                <OptimizedImage
                  src={dashboardImage}
                  alt="SendFeedback dashboard with anonymous feedback analytics and AI insights"
                  className="w-full h-auto"
                  blurPlaceholder
                />
              </div>
              {/* Soft glow under the frame */}
              <div
                aria-hidden
                className="pointer-events-none absolute -bottom-10 left-1/2 -translate-x-1/2 h-24 w-3/4 rounded-full opacity-40 blur-3xl"
                style={{ background: "radial-gradient(closest-side, hsl(var(--primary) / 0.35), transparent)" }}
              />
            </div>
          </div>
        </section>

        {/* THE STAKES — what you're missing */}
        <EditorialSection
          eyebrow="The Stakes"
          title={<>The cost of <span className="italic">not knowing.</span></>}
          intro="For a People team running lean, every preventable resignation is a quarter spent recruiting instead of building. Here's what your annual survey is missing."
          tinted
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <EditorialCard number="01" icon={UserX} title="Quiet quitters, six months early" accent="ochre">
              Top performers mentally check out 6&ndash;12 months before they resign. Annual engagement surveys catch the symptom; anonymous feedback catches the cause &mdash; while there's still time to act.
            </EditorialCard>
            <EditorialCard number="02" icon={ShieldAlert} title="The manager problem no one will name" accent="red">
              <a href="https://www.gallup.com/workplace/232955/no-employee-benefit-no-one-talking.aspx" target="_blank" rel="noopener noreferrer" className="underline decoration-primary/50 hover:text-foreground">50% of employees leave because of their manager</a> (Gallup). In a 100-person company, that's the conversation no one will start in a 1:1 &mdash; or in a survey with their name attached.
            </EditorialCard>
            <EditorialCard number="03" icon={Flame} title="DEI &amp; burnout, off the record" accent="red">
              Harassment, exclusion, unsustainable workloads &mdash; the issues with the highest legal and cultural cost are the exact ones people will only share when they're certain you can't trace it back.
            </EditorialCard>
          </div>
        </EditorialSection>

        {/* SOCIAL PROOF — values */}
        <EditorialSection
          eyebrow="The Pillars"
          title={<>What changes when employees actually <span className="italic">trust the channel.</span></>}
          intro="Four shifts every People leader notices in the first 30 days."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { icon: Eye, stat: "3×", title: "Response rate", label: "Honest channels see ~85% participation vs. ~38% on traditional engagement surveys.", color: "text-destructive", bg: "bg-destructive/10" },
              { icon: Sparkles, stat: "5", title: "Themes, not threads", label: "AI clusters hundreds of comments into the issues actually worth a meeting.", color: "text-warning", bg: "bg-warning/15" },
              { icon: Zap, stat: "90s", title: "Monday brief", label: "No more weekends spent reading verbatims. Skim a brief, act by Tuesday.", color: "text-info", bg: "bg-info/10" },
              { icon: ShieldCheck, stat: "0", title: "IPs or logins", label: "GDPR-ready anonymity Legal will actually sign off on — enforced at the database.", color: "text-success", bg: "bg-success/10" },
            ].map(({ icon: Icon, stat, title, label, color, bg }) => (
              <div key={title} className="rounded-3xl bg-card/90 backdrop-blur-sm border border-border/50 p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] hover:border-primary/30 flex flex-col">
                <div className="flex items-start justify-between mb-6">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${bg} ${color}`}>
                    <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                  </span>
                  <span className={`font-sans text-3xl md:text-4xl font-medium tracking-[-0.02em] tabular-nums ${color}`}>
                    {stat}
                  </span>
                </div>
                <h3 className="font-sans text-lg md:text-xl font-medium tracking-[-0.01em] text-foreground leading-tight mb-2">
                  {title}
                </h3>
                <p className="font-sans text-[15px] text-muted-foreground leading-relaxed">{label}</p>
              </div>
            ))}
          </div>
        </EditorialSection>

        {/* FEATURES */}
        <EditorialSection
          id="features"
          eyebrow="The Methodology"
          title={<>Everything a lean People team needs &mdash; <span className="italic">nothing it doesn't.</span></>}
          intro="No 200-question survey builder. No consultant required. Just the tools to hear what's actually happening and respond without making it worse."
          tinted
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            <EditorialCard number="01" icon={ShieldCheck} title="Anonymity Legal will sign off on" accent="green">
              No login, no IP capture, no browser fingerprint. Database-level enforcement &mdash; not a checkbox in settings. The kind of privacy that surfaces harassment and discrimination before it surfaces in a complaint.
            </EditorialCard>
            <EditorialCard number="02" icon={Brain} title="Never freeze on a hard reply" accent="blue">
              AI-drafted responses in three tones (empathetic, professional, direct) with built-in compliance guardrails for harassment, mental health, and DEI cases. Edit and send &mdash; in minutes, not days.
            </EditorialCard>
            <EditorialCard number="03" icon={Radar} title="Spot flight risk before exit interviews" accent="ochre">
              Pattern detection flags rising sentiment risk by team and theme &mdash; so you can intervene with the manager before a resignation lands in your inbox.
            </EditorialCard>
            <EditorialCard number="04" icon={AlertTriangle} title="Critical alerts the moment they happen" accent="red">
              Harassment, safety, retaliation, legal risk: instant notification with recommended next steps. Nothing slips into a Friday inbox you'll see Monday.
            </EditorialCard>
            <EditorialCard number="05" icon={Scale} title="An HR Advisor on call, 24/7" accent="blue">
              AI guidance trained on employment law and HR best practice. What to say. What never to say. When to bring in counsel. Built for the People team without an in-house ER specialist.
            </EditorialCard>
            <EditorialCard number="06" icon={BarChart3} title="One Monday brief, not a weekend of reading" accent="green">
              Weekly executive summary, theme trends, AI recommendations. Designed to be skim-ready in 90 seconds and forwardable to your CEO without edits.
            </EditorialCard>
          </div>
        </EditorialSection>

        {/* WHY ANONYMOUS-ONLY — manifesto pair */}
        <EditorialSection
          eyebrow="The Vow"
          title={<>Why we are <span className="italic">anonymous-only.</span></>}
          intro={`Most "anonymous" tools have a backdoor. Yours shouldn't be one of them.`}
        >
          <div className="grid md:grid-cols-2 gap-5 md:gap-6">
            <article className="group relative rounded-3xl p-9 md:p-12 bg-card/90 backdrop-blur-sm border border-border/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] hover:border-border flex flex-col">
              <div className="flex items-center justify-between mb-7">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                  <Shield className="h-[18px] w-[18px]" strokeWidth={1.75} />
                </span>
                <span className="font-sans text-[11px] font-medium tracking-[0.18em] uppercase text-muted-foreground tabular-nums">
                  01 / The Promise
                </span>
              </div>
              <h3 className="font-sans text-2xl md:text-[28px] font-medium tracking-[-0.015em] mb-4 text-foreground leading-[1.15]">
                No <span className="italic font-serif font-normal">"optional"</span> anonymity
              </h3>
              <p className="font-sans text-[15px] md:text-base text-muted-foreground leading-relaxed">
                Other platforms let employees "choose" to be anonymous &mdash; which everyone reads as: leadership can probably see who chose to hide. We removed the choice. Everyone is always anonymous. No exceptions, no admin override, no backdoor.
              </p>
            </article>

            <article className="group relative rounded-3xl p-9 md:p-12 bg-foreground text-background border border-foreground transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-20px_rgba(21,34,27,0.45)] flex flex-col">
              <div className="flex items-center justify-between mb-7">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-background/15 text-background">
                  <MessageSquare className="h-[18px] w-[18px]" strokeWidth={1.75} />
                </span>
                <span className="font-sans text-[11px] font-medium tracking-[0.18em] uppercase text-background/60 tabular-nums">
                  02 / The Payoff
                </span>
              </div>
              <h3 className="font-sans text-2xl md:text-[28px] font-medium tracking-[-0.015em] mb-4 text-background leading-[1.15]">
                Honesty, not just <span className="italic font-serif font-normal">response volume</span>
              </h3>
              <p className="font-sans text-[15px] md:text-base text-background/75 leading-relaxed">
                When the channel is genuinely safe, you stop hearing "everything's fine" and start hearing the things that actually decide retention. Optional department tagging gives you the context to act &mdash; without ever exposing the source.
              </p>
            </article>
          </div>
        </EditorialSection>

        {/* FOUNDER LETTER */}
        <EditorialFounderLetter />

        {/* USE CASES */}
        <EditorialSection
          eyebrow="Use Cases"
          title={<>What People leaders <span className="italic">actually catch.</span></>}
          intro="The conversations that don't happen in 1:1s, all-hands, or the engagement survey &mdash; and what changed once they did."
          tinted
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {[
              { tag: "Retention", title: "Regrettable attrition, prevented", quote: "Leadership keeps taking credit for our work.", outcome: "Surfaced 3 months before a senior IC planned to leave. The conversation happened. They stayed.", accent: "green" },
              { tag: "DEI", title: "DEI you can act on", quote: "Women on our team get interrupted 3× more in meetings.", outcome: "Led to manager coaching that moved inclusion scores in a single quarter.", accent: "ochre" },
              { tag: "Remote", title: "Remote disconnection", quote: "I feel invisible working from home.", outcome: "Sparked a remote-rituals overhaul that cut WFH attrition before the next review cycle.", accent: "blue" },
              { tag: "Management", title: "Manager blind spots", quote: "My manager plays favorites and it's destroying morale.", outcome: "Surfaced before an entire pod resigned. Coaching started the same week.", accent: "red" },
              { tag: "Policy", title: "Policy pulse-check", quote: "The new PTO policy feels like a punishment.", outcome: "Reached HR before the all-hands launch. Policy adjusted — and announced — correctly.", accent: "ochre" },
              { tag: "Operations", title: "Process drag on the team", quote: "Our approval process kills creativity.", outcome: "Led to a two-step deletion that gave engineering back roughly a day a week.", accent: "green" },
            ].map(({ tag, title, quote, outcome, accent }) => {
              const accentMap: Record<string, { dot: string; chip: string }> = {
                green: { dot: "bg-primary", chip: "text-primary bg-primary/10" },
                blue: { dot: "bg-[hsl(210_55%_55%)]", chip: "text-[hsl(210_55%_45%)] bg-[hsl(210_55%_55%/0.10)]" },
                ochre: { dot: "bg-[hsl(35_70%_55%)]", chip: "text-[hsl(30_60%_40%)] bg-[hsl(35_70%_55%/0.12)]" },
                red: { dot: "bg-destructive", chip: "text-destructive bg-destructive/10" },
              };
              const a = accentMap[accent];
              return (
                <article
                  key={title}
                  className="group relative rounded-3xl bg-card/90 backdrop-blur-sm border border-border/60 p-7 md:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] hover:border-border flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-5">
                    <span className={`inline-flex h-1.5 w-1.5 rounded-full ${a.dot}`} />
                    <span className={`font-sans text-[10px] uppercase tracking-[0.22em] font-medium px-2 py-1 rounded-full ${a.chip}`}>
                      {tag}
                    </span>
                  </div>

                  <blockquote className="font-serif italic text-[19px] md:text-[21px] leading-[1.4] text-foreground mb-5 relative pl-4 border-l-2 border-border">
                    {quote}
                  </blockquote>

                  <h3 className="font-sans text-[15px] font-medium tracking-[-0.01em] text-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-sans text-[14px] text-muted-foreground leading-relaxed">
                    {outcome}
                  </p>
                </article>
              );
            })}
          </div>
        </EditorialSection>

        {/* HOW IT WORKS */}
        <EditorialSection
          id="how-it-works"
          eyebrow="The Procedure"
          title={<>From silent to <span className="italic">live in 2 minutes.</span></>}
          intro="No SSO project. No IT ticket. No consultant. Built so a People team of one can launch on a Tuesday and have signal by Friday."
        >
          <div className="relative space-y-4 md:space-y-5">
            {[
              { n: "01", meta: "≈ 2 minutes", title: "Spin up your feedback page", body: "Pick a name, set a brand color, copy the link. Drop it into Slack, your handbook, an email, or a QR code on the wall. No employee accounts. No IT involvement." },
              { n: "02", meta: "Continuous", title: "Your team writes what they actually think", body: `No login screen. No "submit as anonymous" checkbox to second-guess. They open the page, write the truth, and hit send &mdash; from any device, on or off the network.` },
              { n: "03", meta: "Overnight", title: "You read the brief and reply privately", body: "AI clusters themes, scores sentiment, and flags risk overnight. Reply through an anonymous thread &mdash; the submitter sees your response without ever revealing themselves." },
            ].map((step) => (
              <div
                key={step.n}
                className="relative grid grid-cols-12 gap-5 md:gap-8 items-start rounded-3xl bg-card/90 backdrop-blur-sm border border-border/60 p-7 md:p-9 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] hover:border-border"
              >
                <div className="col-span-12 sm:col-span-2 flex sm:flex-col sm:items-start items-baseline gap-3 sm:gap-2.5">
                  <span className="font-serif italic text-4xl sm:text-5xl font-normal tabular-nums text-foreground/85 leading-none">
                    {step.n}
                  </span>
                  <span className="font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
                    {step.meta}
                  </span>
                </div>
                <div className="col-span-12 sm:col-span-10 space-y-3">
                  <h3 className="font-sans text-xl sm:text-2xl md:text-[26px] font-medium tracking-[-0.015em] text-foreground leading-[1.2]">
                    {step.title}
                  </h3>
                  <p
                    className="font-sans text-[15px] md:text-base text-muted-foreground leading-relaxed max-w-2xl"
                    dangerouslySetInnerHTML={{ __html: step.body }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Outcomes panel */}
          <div className="mt-14 md:mt-20 rounded-[28px] md:rounded-[36px] border border-border/60 p-8 md:p-12 bg-gradient-to-br from-secondary/60 via-secondary/30 to-card/60 backdrop-blur-sm">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex h-2 w-2 rounded-full bg-primary" />
                  <span className="font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
                    The 90-day mark
                  </span>
                </div>
                <h3 className="font-sans text-2xl md:text-[32px] font-medium tracking-[-0.02em] text-foreground leading-[1.15] max-w-xl">
                  What People teams report
                  <br />
                  after <span className="font-serif italic font-normal text-primary">90 days.</span>
                </h3>
              </div>
              <span className="font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground tabular-nums">
                Six recurring outcomes
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
              {[
                "Culture issues caught months before the engagement survey would have",
                "Regrettable resignations prevented through earlier intervention",
                "Hours of manual verbatim-reading replaced by AI Monday briefs",
                "Higher participation than annual or quarterly engagement surveys",
                "Manager-level issues surfaced before they trigger team-wide turnover",
                "A defensible paper trail when harassment or DEI cases escalate",
              ].map((line, i) => (
                <div
                  key={line}
                  className="group flex items-start gap-4 rounded-2xl bg-card/80 border border-border/50 p-5 transition-all duration-300 hover:bg-card hover:border-primary/30 hover:-translate-y-0.5"
                >
                  <span className="font-sans text-[11px] font-medium tabular-nums text-muted-foreground/70 mt-1 w-6 flex-shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary flex-shrink-0 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </span>
                  <span className="font-sans text-[15px] text-foreground leading-[1.5]">
                    {line}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </EditorialSection>

        {/* COMPARISON */}
        <div className="bg-secondary/30 border-t border-border/40">
          <ComparisonTable />
        </div>

        {/* TESTIMONIALS */}
        <EditorialSection
          eyebrow="Customer Stories"
          title={<>Real results from <span className="italic">real companies.</span></>}
          intro="Leaders who solved critical culture issues before they became expensive."
          tinted
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 items-stretch">
            {[
              { quote: "We caught a manager bullying situation that would've cost us 5 team members and a discrimination lawsuit. ROI in month one.", pull: "ROI in month one.", name: "Sarah Mitchell", role: "Head of People", co: "CloudScale Software", img: testimonial1, tag: "Risk caught" },
              { quote: "Our retention improved significantly after we started using this. Turns out people will stay when they feel heard — and safe.", pull: "people will stay when they feel heard.", name: "James Cooper", role: "CEO", co: "RemoteFirst HQ", img: testimonial2, tag: "Retention" },
              { quote: "Finally found out why our best engineers were leaving. It wasn't salary — it was a toxic product manager. We fixed it. They stayed.", pull: "It wasn't salary.", name: "David Chen", role: "VP of Engineering", co: "PayStream Technologies", img: testimonial3, tag: "Root cause" },
            ].map((t, i) => (
              <article
                key={t.name}
                className="group relative flex flex-col rounded-3xl bg-card/90 backdrop-blur-sm border border-border/60 p-7 md:p-8 h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] hover:border-primary/30"
              >
                {/* Decorative serif quote mark */}
                <span
                  aria-hidden
                  className="absolute top-5 right-6 font-serif text-[80px] leading-none text-primary/12 select-none pointer-events-none"
                >
                  &ldquo;
                </span>

                <div className="flex items-center justify-between mb-6 relative">
                  <span className="inline-flex items-center gap-1.5 font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                    {t.tag}
                  </span>
                  <span className="font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-muted-foreground/70 tabular-nums">
                    {String(i + 1).padStart(2, "0")} / 03
                  </span>
                </div>

                <blockquote className="font-serif italic text-[24px] md:text-[26px] leading-[1.2] text-foreground mb-5 tracking-[-0.01em] relative">
                  &ldquo;{t.pull}&rdquo;
                </blockquote>

                <p className="font-sans text-[14px] text-muted-foreground leading-[1.65] mb-7 flex-1 relative">
                  {t.quote}
                </p>

                {/* Soft divider rule */}
                <div className="relative mt-auto pt-5">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                  <div className="flex items-center gap-3">
                    <img src={t.img} alt={t.name} className="w-11 h-11 object-cover rounded-full flex-shrink-0 ring-2 ring-background shadow-sm" />
                    <div className="min-w-0 flex-1">
                      <div className="font-sans text-[13.5px] font-medium tracking-[-0.005em] text-foreground truncate">{t.name}</div>
                      <div className="font-sans text-[11.5px] text-muted-foreground truncate mt-0.5">
                        {t.role} <span className="text-muted-foreground/40">·</span> {t.co}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-0.5 flex-shrink-0">
                      {[0,1,2,3,4].map((s) => (
                        <span key={s} className="h-1 w-1 rounded-full bg-primary" />
                      ))}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12">
            <SourcesCitation className="max-w-4xl mx-auto" />
          </div>
        </EditorialSection>

        {/* PRICING */}
        <div className="bg-background border-t border-border/40">
          <PricingPreview />
        </div>

        {/* FAQ */}
        <EditorialSection
          id="faq"
          eyebrow="FAQ"
          title={<>Questions <span className="italic">leaders ask.</span></>}
          intro="Everything you need to know before getting started — answered plainly, without the marketing varnish."
          tinted
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Sidebar — "Ask the desk" */}
            <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
              <div className="relative rounded-[28px] bg-card/90 backdrop-blur-sm border border-border/60 p-7 md:p-8 shadow-[0_24px_60px_-30px_rgba(21,34,27,0.12)] overflow-hidden">
                {/* Soft accent halo */}
                <div className="pointer-events-none absolute -top-20 -right-16 h-48 w-48 rounded-full bg-primary/8 blur-3xl" aria-hidden />

                <div className="relative">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="inline-flex h-2 w-2 rounded-full bg-primary" />
                    <span className="font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
                      Ask the desk
                    </span>
                  </div>

                  <h3 className="font-sans text-xl md:text-[26px] font-medium tracking-[-0.015em] leading-[1.15] text-foreground mb-3">
                    Didn't find your{" "}
                    <span className="font-serif italic font-normal text-primary">answer?</span>
                  </h3>
                  <p className="font-sans text-[14.5px] text-muted-foreground leading-relaxed mb-6">
                    Write to us. We reply within one business day &mdash; from a real person, not a ticket queue.
                  </p>

                  <a
                    href="mailto:hello@sendfeedback.ai"
                    className="group/mail inline-flex max-w-full items-center gap-2 rounded-full bg-foreground text-background px-4 sm:px-5 py-2.5 font-sans text-[13px] font-medium tracking-[-0.005em] hover:bg-primary transition-colors duration-300"
                  >
                    <span className="truncate">hello@sendfeedback.ai</span>
                    <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 transition-transform group-hover/mail:translate-x-0.5" strokeWidth={1.75} />
                  </a>

                  {/* Trust meta */}
                  <div className="mt-7 pt-6 border-t border-border/60 grid grid-cols-2 gap-4">
                    <div>
                      <div className="font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-muted-foreground mb-1">
                        Reply time
                      </div>
                      <div className="font-sans text-[15px] font-medium tracking-[-0.005em] text-foreground tabular-nums">
                        &lt; 1 business day
                      </div>
                    </div>
                    <div>
                      <div className="font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-muted-foreground mb-1">
                        From
                      </div>
                      <div className="font-sans text-[15px] font-medium tracking-[-0.005em] text-foreground">
                        A real person
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Questions column */}
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible className="space-y-3">
                {[
                  { icon: DollarSign, q: "What's the ROI?", a: "If this tool prevents even one regrettable departure, you've saved significant costs &mdash; SHRM estimates turnover costs 50&ndash;200% of annual salary in recruiting, training, and lost productivity. Plus: hours saved monthly in feedback analysis, earlier problem detection, and better decision-making data.", color: "text-success", bg: "bg-success/10" },
                  { icon: AlertTriangle, q: "Won't this create more problems than it solves?", a: "The opposite. You already have these problems &mdash; you just don't see them yet. Anonymous feedback surfaces issues while they're still fixable. Ignoring them doesn't make them go away; it makes them expensive.", color: "text-destructive", bg: "bg-destructive/10" },
                  { icon: Zap, q: "How is this different from engagement surveys?", a: "Engagement surveys are like yearly physicals &mdash; they catch what's already wrong. SendFeedback is a continuous health monitor that spots issues while they're still treatable. Anonymous feedback gets significantly higher participation because employees feel safe to share openly.", color: "text-warning", bg: "bg-warning/15" },
                  { icon: Shield, q: "What if people abuse anonymity?", a: "Our AI-powered abuse detection and PII filtering keeps feedback constructive. People want to help, not hurt &mdash; when they have a safe channel.", color: "text-info", bg: "bg-info/10" },
                  { icon: Lock, q: "How is anonymity actually protected?", a: "We don't collect IP addresses, browser fingerprints, login credentials, or any identifying metadata. Even we can't tell who submitted what. It's legal-grade protection that employees actually trust.", color: "text-info", bg: "bg-info/10" },
                  { icon: Download, q: "Can we export and own our data?", a: "Yes. Export feedback, summaries, and insights anytime in multiple formats. You own your data completely &mdash; no lock-in, no limitations.", color: "text-success", bg: "bg-success/10" },
                ].map(({ icon: Icon, q, a, color, bg }, i) => (
                  <AccordionItem
                    key={q}
                    value={`faq-${i}`}
                    className="rounded-2xl border border-border/60 bg-card/90 backdrop-blur-sm px-5 md:px-6 transition-all duration-300 hover:border-border group/item data-[state=open]:border-primary/40 data-[state=open]:shadow-[0_24px_60px_-30px_rgba(62,92,77,0.18)]"
                  >
                    <AccordionTrigger className="py-5 md:py-6 hover:no-underline [&>svg]:hidden">
                      <div className="flex items-center gap-4 text-left w-full">
                        <span className="font-sans text-[11px] font-medium tabular-nums text-muted-foreground/70 flex-shrink-0 w-6">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${bg} ${color} flex-shrink-0`}>
                          <Icon className="h-[16px] w-[16px]" strokeWidth={1.75} />
                        </span>
                        <span className="flex-1 font-sans text-base sm:text-[17px] md:text-[19px] font-medium tracking-[-0.01em] text-foreground group-hover/item:text-primary transition-colors leading-snug">
                          {q}
                        </span>
                        <span
                          aria-hidden
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-foreground/70 font-sans text-lg leading-none flex-shrink-0 transition-all duration-300 group-data-[state=open]/item:rotate-45 group-data-[state=open]/item:bg-primary group-data-[state=open]/item:text-primary-foreground"
                        >
                          +
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pl-[76px] pr-12">
                      <p
                        className="font-sans text-[15px] md:text-base text-muted-foreground leading-relaxed max-w-2xl"
                        dangerouslySetInnerHTML={{ __html: a }}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </EditorialSection>

        {/* FINAL CTA — Atrium luminous closer */}
        <section className="relative px-4 sm:px-6 md:px-10 py-16 sm:py-24 md:py-32 overflow-hidden">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-0"
            style={{
              background:
                "radial-gradient(900px 500px at 90% 10%, hsl(50 60% 92% / 0.6), transparent 60%), radial-gradient(800px 500px at 10% 100%, hsl(150 30% 88% / 0.5), transparent 60%)",
            }}
          />
          <div className="relative max-w-6xl mx-auto">
            <div className="rounded-[1.5rem] sm:rounded-[2rem] bg-foreground text-background p-6 sm:p-12 md:p-16 lg:p-20 overflow-hidden relative shadow-[0_60px_120px_-40px_rgba(21,34,27,0.4)]">
              {/* Inner ambient glow */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(700px 380px at 90% 0%, hsl(50 60% 75% / 0.18), transparent 60%), radial-gradient(600px 400px at 0% 100%, hsl(150 40% 50% / 0.18), transparent 60%)",
                }}
              />

              <div className="relative">
                <div className="flex items-center justify-between gap-4 flex-wrap mb-8 md:mb-10">
                  <span className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/10 backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-background/85">
                    <span className="h-1.5 w-1.5 rounded-full bg-background animate-pulse" />
                    For People &amp; HR Leaders
                  </span>
                  <span className="font-sans text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-background/55 tabular-nums">
                    The Final Note
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
                  <div className="lg:col-span-8">
                    <h2 className="font-sans text-[34px] xs:text-[38px] sm:text-5xl md:text-6xl lg:text-[76px] leading-[1.04] sm:leading-[1.0] tracking-[-0.025em] font-medium text-background">
                      Stop finding out
                      <span className="block font-serif italic font-normal text-background/85">
                        at the exit interview.
                      </span>
                    </h2>
                    <p className="mt-6 md:mt-8 font-sans text-base md:text-lg text-background/70 leading-relaxed max-w-xl">
                      The truth is already in your team. The only question is whether you hear it on a Tuesday &mdash; or in a resignation letter.
                    </p>
                  </div>
                  <aside className="lg:col-span-4 lg:pl-8 lg:border-l border-background/15">
                    <div className="font-sans text-[10px] uppercase tracking-[0.22em] font-medium text-background/55 mb-3">
                      The Promise
                    </div>
                    <p className="font-sans text-base md:text-[17px] text-background/80 leading-relaxed">
                      Launch your honest channel in <span className="text-background font-medium">two minutes</span>. Read the first real feedback by <span className="text-background font-medium">Friday</span>. Decide after 15 days &mdash; not 15 months.
                    </p>
                  </aside>
                </div>

                <div className="mt-12 md:mt-16 pt-8 border-t border-background/15 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <button
                      onClick={() => navigate("/auth?mode=signup")}
                      className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium tracking-[-0.005em] hover:shadow-[0_20px_50px_-12px_hsl(var(--primary)/0.5)] hover:-translate-y-0.5 transition-all duration-300 min-h-[52px] w-full sm:w-auto"
                    >
                      Start listening honestly
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.75} />
                    </button>
                    <button
                      onClick={() => navigate("/pricing")}
                      className="inline-flex items-center justify-center gap-2.5 rounded-full border border-background/25 bg-background/5 backdrop-blur text-background px-7 py-4 text-sm font-medium tracking-[-0.005em] hover:bg-background/12 hover:-translate-y-0.5 transition-all duration-300 min-h-[52px] w-full sm:w-auto"
                    >
                      See pricing
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[12px] font-medium text-background/70">
                    {[
                      "15-day trial",
                      "No card required",
                      "Cancel anytime",
                    ].map((item, i, arr) => (
                      <span key={item} className="flex items-center gap-3">
                        <span className="flex items-center gap-1.5">
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-background/15">
                            <Check className="h-2.5 w-2.5 text-background" strokeWidth={2.5} />
                          </span>
                          {item}
                        </span>
                        {i < arr.length - 1 && <span className="text-background/25">·</span>}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <EditorialFooter />
      <StickyCTA />
    </div>
  );
};

export default Index;
